import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { CognitoAuthService } from "../services/cognitoService";
import { fetchUserById } from "../features/user-management/services/userService";
import { persistUiThemePreference } from "../services/userThemePreferenceService";
import {
  DEFAULT_THEME_PALETTE_ID,
  type ThemeMode,
  type ResolvedAppearance,
  type ThemePaletteId,
  readStoredThemeMode,
  resolveAppearance,
  applyAppearanceClass,
  applySagelynContrastToDocument,
  sagelynContrastForThemePreference,
  getSystemColorSchemeMql,
  isThemeMode,
  THEME_STORAGE_KEY,
} from "../theme/themeTokens";

export type { ThemeMode };

interface ThemeContextProps {
  theme: ThemeMode;
  /** Updates preference, localStorage, DOM, and optionally the users API (unless fromServer). */
  setTheme: (mode: ThemeMode, options?: { fromServer?: boolean }) => void;
  activeTheme: ResolvedAppearance;
  /** Identifier for the active color palette (extensible for brand / tenant themes). */
  themePaletteId: ThemePaletteId;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

/**
 * Maps common light Tailwind utility classes to Sagelyn dark appearance without rewriting every screen.
 */
const darkOverrides = `
  html.dark {
    color-scheme: dark;
  }
  html.dark body {
    background-color: var(--color-background) !important;
    color: var(--color-text-primary) !important;
  }

  html.dark input, html.dark select, html.dark textarea {
    background-color: var(--color-input-bg) !important;
    color: var(--color-text-primary) !important;
    border-color: var(--color-input-border) !important;
  }
  /* Chat composer: single rounded surface (wrapper is bg-theme-surface); global textarea fill caused a “double box” in dark mode */
  html.dark textarea.chat-composer-textarea {
    background-color: transparent !important;
    border-color: transparent !important;
  }
  html.dark input::placeholder, html.dark textarea::placeholder {
    color: var(--color-text-secondary) !important;
  }

  html.dark .bg-white { background-color: var(--color-surface) !important; }
  html.dark .bg-white\\/90 { background-color: color-mix(in srgb, var(--color-surface) 90%, transparent) !important; }
  html.dark .bg-white\\/80 { background-color: color-mix(in srgb, var(--color-surface) 80%, transparent) !important; }
  html.dark .bg-white\\/60 { background-color: color-mix(in srgb, var(--color-surface) 60%, transparent) !important; }
  html.dark .bg-white\\/50 { background-color: color-mix(in srgb, var(--color-surface) 50%, transparent) !important; }
  html.dark .bg-surface-ui { background-color: var(--color-surface) !important; }
  html.dark .bg-surface-ui-hover { background-color: var(--color-surface-ui-hover) !important; }

  html.dark .bg-zinc-50, html.dark .bg-gray-50, html.dark .bg-slate-50 { background-color: color-mix(in srgb, var(--color-surface) 55%, var(--color-background)) !important; }
  html.dark .bg-zinc-50\\/80, html.dark .bg-gray-50\\/80, html.dark .bg-slate-50\\/80 { background-color: color-mix(in srgb, var(--color-surface) 68%, var(--color-background)) !important; }
  html.dark .bg-zinc-50\\/60, html.dark .bg-gray-50\\/60, html.dark .bg-slate-50\\/60 { background-color: color-mix(in srgb, var(--color-surface) 62%, var(--color-background)) !important; }
  html.dark .bg-zinc-50\\/50, html.dark .bg-gray-50\\/50, html.dark .bg-slate-50\\/50 { background-color: color-mix(in srgb, var(--color-surface) 58%, var(--color-background)) !important; }
  html.dark .bg-zinc-100, html.dark .bg-gray-100, html.dark .bg-slate-100 { background-color: var(--color-surface) !important; }
  html.dark .bg-zinc-200, html.dark .bg-gray-200, html.dark .bg-slate-200 { background-color: var(--color-surface-ui-hover) !important; }

  html.dark .text-brand-primary { color: var(--color-brand-primary) !important; }
  html.dark .text-zinc-900, html.dark .text-gray-900, html.dark .text-slate-900 { color: #dadada !important; }
  html.dark .text-zinc-800, html.dark .text-gray-800, html.dark .text-slate-800 { color: #dadada !important; }
  html.dark .text-zinc-700, html.dark .text-gray-700, html.dark .text-slate-700 { color: #dadada !important; }
  html.dark .text-zinc-600, html.dark .text-gray-600, html.dark .text-slate-600 { color: #dadada !important; }
  html.dark .text-zinc-500, html.dark .text-gray-500, html.dark .text-slate-500 { color: #dadada !important; }
  html.dark .text-zinc-400, html.dark .text-gray-400, html.dark .text-slate-400 { color: #dadada !important; }
  html.dark .text-black { color: #dadada !important; }

  /* New-chat welcome + in-thread “Suggestions”: label + horizontal rules (readable on dark chat surfaces) */
  html.dark .chat-welcome-suggestions-heading { color: #dadada !important; }
  html.dark .chat-welcome-suggestions-rule { background-color: rgba(228, 228, 231, 0.45) !important; }

  /* In-thread suggestion pills (ActionButtons) — visible containers vs #27272a-style backgrounds */
  html.dark .chat-suggestion-pill-primary {
    background-color: rgba(100, 139, 127, 0.3) !important;
    border-color: rgba(142, 181, 168, 0.75) !important;
  }
  html.dark .chat-suggestion-pill-primary:hover {
    background-color: rgba(100, 139, 127, 0.42) !important;
    border-color: rgba(196, 230, 214, 0.88) !important;
  }
  html.dark .chat-suggestion-pill-accent {
    background-color: rgba(228, 179, 114, 0.26) !important;
    border-color: rgba(251, 191, 36, 0.62) !important;
  }
  html.dark .chat-suggestion-pill-accent:hover {
    background-color: rgba(228, 179, 114, 0.38) !important;
    border-color: rgba(253, 224, 178, 0.82) !important;
  }

  /* Empty-state starter pills (NewChatWelcomeSuggestions) — separate from thread pills */
  html.dark .new-chat-starter-pill-neutral {
    background-color: var(--color-surface) !important;
    border-color: rgba(161, 161, 170, 0.75) !important;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.07) !important;
  }
  html.dark .new-chat-starter-pill-neutral:hover {
    background-color: var(--color-surface-ui-hover) !important;
    border-color: rgba(212, 212, 216, 0.6) !important;
  }
  html.dark .new-chat-starter-pill-accent {
    background-color: rgba(92, 77, 63, 0.98) !important;
    border-color: rgba(251, 191, 36, 0.55) !important;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06) !important;
  }
  html.dark .new-chat-starter-pill-accent:hover {
    background-color: rgba(115, 98, 80, 0.98) !important;
    border-color: rgba(252, 211, 77, 0.72) !important;
  }

  html.dark .border-\\[\\#2f4941\\]\\/30 { border-color: rgba(228, 228, 231, 0.4) !important; }
  html.dark .border-\\[\\#2f4941\\]\\/40 { border-color: rgba(228, 228, 231, 0.4) !important; }
  html.dark .border-\\[\\#2f4941\\]\\/50 { border-color: rgba(228, 228, 231, 0.4) !important; }

  /* Alerts and Banners (Green, Blue, Yellow — red uses index.css --color-danger utilities) */

  html.dark .bg-green-50 { background-color: rgba(34, 197, 94, 0.15) !important; }
  html.dark .border-green-200 { border-color: rgba(34, 197, 94, 0.3) !important; }
  html.dark .text-green-700 { color: #86efac !important; }

  html.dark .bg-blue-50 { background-color: rgba(59, 130, 246, 0.15) !important; }
  html.dark .border-blue-400 { border-color: rgba(59, 130, 246, 0.4) !important; }
  html.dark .text-blue-800 { color: #93c5fd !important; }

  html.dark .bg-yellow-100 { background-color: rgba(234, 179, 8, 0.2) !important; }
  html.dark .text-yellow-800 { color: #fef08a !important; }
  html.dark .bg-green-100 { background-color: rgba(34, 197, 94, 0.2) !important; }
  html.dark .text-green-800 { color: #bbf7d0 !important; }
  html.dark .bg-blue-100 { background-color: rgba(59, 130, 246, 0.2) !important; }
  html.dark .text-blue-800 { color: #bfdbfe !important; }

  html.dark .hover\\:bg-white:hover { background-color: var(--color-surface) !important; }
  html.dark .hover\\:bg-white\\/60:hover { background-color: color-mix(in srgb, var(--color-surface) 60%, transparent) !important; }
  html.dark .hover\\:bg-white\\/80:hover { background-color: color-mix(in srgb, var(--color-surface) 80%, transparent) !important; }
  html.dark .hover\\:bg-white\\/90:hover { background-color: color-mix(in srgb, var(--color-surface) 90%, transparent) !important; }
  html.dark .hover\\:bg-zinc-50:hover, html.dark .hover\\:bg-gray-50:hover { background-color: var(--color-surface) !important; }
  html.dark .hover\\:bg-zinc-100:hover, html.dark .hover\\:bg-gray-100:hover { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .hover\\:bg-surface-ui-hover:hover { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .hover\\:text-zinc-900:hover, html.dark .hover\\:text-gray-900:hover { color: #dadada !important; }
  html.dark .hover\\:text-zinc-700:hover, html.dark .hover\\:text-gray-700:hover { color: #dadada !important; }
  /* Button hover → secondary */
  html.dark .hover\\:bg-theme-secondary\\/15:hover { background-color: rgba(131, 177, 171, 0.18) !important; }
  html.dark .hover\\:bg-theme-secondary\\/20:hover { background-color: rgba(131, 177, 171, 0.22) !important; }

  html.dark .border-zinc-100, html.dark .border-gray-100, html.dark .border-slate-100 { border-color: var(--color-border) !important; }
  html.dark .border-zinc-200, html.dark .border-gray-200, html.dark .border-slate-200 { border-color: var(--color-border) !important; }
  html.dark .border-zinc-200\\/80 { border-color: color-mix(in srgb, var(--color-border) 85%, transparent) !important; }
  html.dark .border-zinc-200\\/90 { border-color: color-mix(in srgb, var(--color-border) 92%, transparent) !important; }
  html.dark .border-zinc-300, html.dark .border-gray-300, html.dark .border-slate-300 { border-color: #52525b !important; }
  html.dark .ring-zinc-200 { --tw-ring-color: #7daaa7 !important; }
  html.dark .ring-zinc-200\\/50 { --tw-ring-color: rgba(125, 170, 167, 0.45) !important; }
  html.dark .ring-zinc-200\\/60 { --tw-ring-color: rgba(125, 170, 167, 0.5) !important; }
  html.dark .ring-zinc-200\\/80 { --tw-ring-color: rgba(125, 170, 167, 0.55) !important; }

  html.dark ::-webkit-scrollbar { width: 10px; height: 10px; }
  html.dark ::-webkit-scrollbar-track { background: var(--color-background); }
  html.dark ::-webkit-scrollbar-thumb { background: var(--color-surface-ui-hover); border-radius: 5px; }
  html.dark ::-webkit-scrollbar-thumb:hover { background: color-mix(in srgb, var(--color-surface-ui-hover) 85%, var(--color-text-secondary)); }
  html.dark input:-webkit-autofill, html.dark input:-webkit-autofill:hover,
  html.dark input:-webkit-autofill:focus, html.dark input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px var(--color-surface) inset !important;
    -webkit-text-fill-color: #dadada !important;
  }

  html.dark .shadow, html.dark .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.45) !important; border: 1px solid var(--color-border); }
  html.dark .shadow-md { box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.45) !important; border: 1px solid var(--color-border); }
  html.dark .shadow-lg { box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.45) !important; border: 1px solid var(--color-border); }

  html.dark img[src*="yt_logo"] { filter: drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.8)); }
  html.dark img[src*="LI-Logo"] { filter: brightness(1.5) drop-shadow(0px 0px 4px rgba(255, 255, 255, 0.2)); }
  /* WordPress logotype is a solid-dark wordmark — invert it to white so it stays visible on dark surfaces */
  html.dark img[src*="WordPress-logotype"] { filter: brightness(0) invert(1); }
  
  /* Brand Management Custom Hex Colors */
  html.dark .bg-\\[\\#eae6da\\] { background-color: var(--color-surface) !important; }
  html.dark .text-\\[\\#2f4941\\] { color: #dadada !important; }
  html.dark .text-\\[\\#2f4941\\]\\/60 { color: rgba(218, 218, 218, 0.6) !important; }
  html.dark .text-\\[\\#2f4941\\]\\/70 { color: rgba(218, 218, 218, 0.88) !important; }
  html.dark .bg-\\[\\#2f4941\\]\\/5 { background-color: rgba(244, 244, 245, 0.08) !important; }
  /* Starter suggestion pills (brand tints) */
  html.dark .bg-\\[\\#e4b372\\]\\/10 { background-color: rgba(228, 179, 114, 0.2) !important; }
  html.dark .border-\\[\\#e4b372\\]\\/10 { border-color: rgba(228, 179, 114, 0.45) !important; }
  html.dark .hover\\:bg-\\[\\#e4b372\\]\\/20:hover { background-color: rgba(228, 179, 114, 0.3) !important; }
  html.dark .bg-\\[\\#648b7f\\]\\/10 { background-color: rgba(142, 181, 168, 0.22) !important; }
  html.dark .border-\\[\\#648b7f\\]\\/10 { border-color: rgba(142, 181, 168, 0.5) !important; }
  html.dark .hover\\:bg-\\[\\#648b7f\\]\\/20:hover { background-color: rgba(142, 181, 168, 0.34) !important; }
  html.dark .text-\\[\\#648b7f\\] { color: #dadada !important; }
  /* Sage button fills: #2f4941 light / #4a6b62 dark. White on #648b7f is only 3.8:1 (SGL-585), so
     dark fills use the deeper sage (5.9:1), matching .bg-brand-primary in index.css. */
  html.dark .bg-\\[\\#648b7f\\] { background-color: #4a6b62 !important; }
  html.dark .bg-\\[\\#2f4941\\] { background-color: #4a6b62 !important; }
  html.dark .border-\\[\\#648b7f\\] { border-color: #648b7f !important; }
  html.dark .border-\\[\\#2f4941\\] { border-color: #648b7f !important; }
  html.dark .hover\\:bg-\\[\\#648b7f\\]:hover { background-color: #557a6f !important; }
  html.dark .hover\\:bg-\\[\\#1e3128\\]:hover { background-color: #557a6f !important; }
  html.dark .hover\\:bg-\\[\\#293f39\\]:hover { background-color: #557a6f !important; }
  html.dark .focus\\:ring-\\[\\#648b7f\\]\\/30:focus { --tw-ring-color: color-mix(in srgb, #648b7f 30%, transparent) !important; }
  html.dark .focus\\:ring-\\[\\#2f4941\\]:focus { --tw-ring-color: color-mix(in srgb, #648b7f 30%, transparent) !important; }
  html.dark .focus\\:border-\\[\\#648b7f\\]:focus { border-color: #648b7f !important; }

  /* Pre-auth screens (Login/NewPasswordRequired/MfaChallenge/ForgotPassword/VerifyEmail) — sage text/button, missing from the Brand Management remap above */
  html.dark .text-\\[\\#3e5c54\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#3e5c54\\]\\/80 { color: rgba(218, 218, 218, 0.8) !important; }
  html.dark .text-\\[\\#3e5c54\\]\\/70 { color: rgba(218, 218, 218, 0.7) !important; }
  html.dark .text-\\[\\#3e5c54\\]\\/60 { color: rgba(218, 218, 218, 0.6) !important; }
  html.dark .text-\\[\\#3e5c54\\]\\/50 { color: rgba(218, 218, 218, 0.5) !important; }
  html.dark .placeholder\\:text-\\[\\#3e5c54\\]\\/50::placeholder { color: rgba(218, 218, 218, 0.5) !important; }
  html.dark .hover\\:text-\\[\\#3e5c54\\]:hover { color: var(--color-text-primary) !important; }
  html.dark .hover\\:text-\\[\\#2d453e\\]:hover { color: var(--color-text-primary) !important; }
  html.dark .bg-\\[\\#3e5c54\\] { background-color: var(--color-brand-primary) !important; }
  html.dark .hover\\:bg-\\[\\#2d453e\\]:hover { background-color: var(--color-brand-primary-hover) !important; }
  html.dark .focus-visible\\:outline-\\[\\#3e5c54\\] { outline-color: var(--color-brand-primary) !important; }

  /* Chat shell / split panel (NewChatWindow non-embedded cream). System: same rule via systemDarkOverrides below. */
  html.dark .bg-\\[\\#f1efea\\] { background-color: var(--color-background) !important; }
  html.dark .bg-\\[\\#FAFAFA\\], html.dark .bg-\\[\\#fafafa\\] { background-color: var(--color-background) !important; }
  html.dark .border-\\[\\#e0dbcf\\] { border-color: var(--color-border) !important; }

  /* ─────────────────────────────────────────────────────────────────────
   *  Video creation surfaces (UGC modal/editor/timeline/review-mode +
   *  generate-video forms). Page surfaces, cool-grey text/border hexes,
   *  solid greys (300..900), and timeline lane colors that previously had
   *  no dark mapping. Keeps Sagelyn dark consistent across video flows.
   * ──────────────────────────────────────────────────────────────────── */

  /* Cool-grey page surfaces: lift to canvas/surface so screens stop showing through as cream/light */
  html.dark .bg-\\[\\#F9FAFB\\], html.dark .bg-\\[\\#f9fafb\\] { background-color: var(--color-background) !important; }
  html.dark .bg-\\[\\#f7fafc\\] { background-color: var(--color-background) !important; }
  html.dark .bg-\\[\\#f5f4f0\\] { background-color: var(--color-background) !important; }
  html.dark .bg-\\[\\#dedad4\\] { background-color: var(--color-surface) !important; }
  html.dark .bg-\\[\\#F3F4F6\\], html.dark .bg-\\[\\#f3f4f6\\] { background-color: var(--color-surface) !important; }
  html.dark .bg-\\[\\#f0f4f2\\] { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-\\[\\#f1f5f9\\] { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-\\[\\#fffbeb\\] { background-color: color-mix(in srgb, var(--color-accent-warm) 15%, var(--color-surface)) !important; }
  html.dark .hover\\:bg-\\[\\#fffbeb\\]:hover { background-color: color-mix(in srgb, var(--color-accent-warm) 15%, var(--color-surface)) !important; }
  /* Editor "selected segment" pinned panel gradient: bg-gradient-to-b from-[#f0f4f2] to-white */
  html.dark .from-\\[\\#f0f4f2\\] { --tw-gradient-from: var(--color-surface-ui-hover) !important; }
  html.dark .from-white { --tw-gradient-from: var(--color-surface) !important; }
  html.dark .to-white { --tw-gradient-to: var(--color-surface) !important; }
  html.dark .to-\\[\\#FAFAFA\\], html.dark .to-\\[\\#fafafa\\] { --tw-gradient-to: var(--color-background) !important; }
  html.dark .hover\\:bg-\\[\\#f0f4f2\\]:hover { background-color: var(--color-surface-ui-hover) !important; }

  /* Custom hex text greys: lift to theme-fg / theme-fg-muted (hierarchy collapses but consistent w/ existing gray-* remap) */
  html.dark .text-\\[\\#1F2937\\], html.dark .text-\\[\\#1f2937\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#333333\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#374151\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#111827\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#6B7280\\], html.dark .text-\\[\\#6b7280\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#9CA3AF\\], html.dark .text-\\[\\#9ca3af\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#94a3b8\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#64748b\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#475569\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#1e293b\\] { color: var(--color-text-primary) !important; }
  html.dark .text-\\[\\#57534e\\] { color: var(--color-text-secondary) !important; }
  html.dark .text-\\[\\#b8832a\\] { color: var(--color-accent-warm) !important; }
  html.dark .text-\\[\\#0369a1\\] { color: #7dd3fc !important; }
  html.dark .text-\\[\\#92400e\\] { color: #fcd34d !important; }
  html.dark .text-\\[\\#333333\\]\\/60 { color: rgba(218, 218, 218, 0.6) !important; }
  html.dark .text-\\[\\#333333\\]\\/40 { color: rgba(218, 218, 218, 0.4) !important; }
  html.dark .hover\\:text-\\[\\#1F2937\\]:hover, html.dark .hover\\:text-\\[\\#374151\\]:hover { color: var(--color-text-primary) !important; }
  html.dark .placeholder\\:text-\\[\\#9CA3AF\\]::placeholder { color: var(--color-text-secondary) !important; }

  /* Custom hex borders (UGC tabs / inputs / cards) */
  html.dark .border-\\[\\#E5E5E5\\] { border-color: var(--color-border) !important; }
  html.dark .border-\\[\\#E5E7EB\\] { border-color: var(--color-border) !important; }
  html.dark .border-\\[\\#B7793B\\] { border-color: var(--color-accent-warm) !important; }
  html.dark .border-\\[\\#D1D5DB\\] { border-color: var(--color-border) !important; }
  html.dark .hover\\:border-\\[\\#D1D5DB\\]:hover { border-color: var(--color-border) !important; }
  html.dark .focus\\:border-\\[\\#e4b372\\]:focus { border-color: var(--color-accent-warm) !important; }
  html.dark .focus\\:ring-\\[\\#e4b372\\]\\/25:focus { --tw-ring-color: color-mix(in srgb, var(--color-accent-warm) 35%, transparent) !important; }

  /* Selected-card warm tint — format/engine/resolution/expressiveness selectors */
  html.dark .bg-\\[\\#FFF8F1\\] { background-color: color-mix(in srgb, var(--color-accent-warm) 18%, var(--color-surface)) !important; }
  html.dark .bg-\\[\\#FDEBD7\\] { background-color: color-mix(in srgb, var(--color-accent-warm) 22%, var(--color-surface)) !important; }
  html.dark .bg-\\[\\#F5F5F5\\] { background-color: var(--color-background) !important; }
  html.dark .hover\\:bg-\\[\\#E5E5E5\\]:hover { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .hover\\:bg-\\[\\#F8FAFC\\]:hover { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-\\[\\#B7793B\\]\\/5 { background-color: color-mix(in srgb, var(--color-accent-warm) 8%, var(--color-surface)) !important; }
  html.dark .ring-\\[\\#B7793B\\]\\/15 { --tw-ring-color: color-mix(in srgb, var(--color-accent-warm) 25%, transparent) !important; }
  html.dark .ring-\\[\\#B7793B\\]\\/25 { --tw-ring-color: color-mix(in srgb, var(--color-accent-warm) 35%, transparent) !important; }

  /* Accent-warm text and interactive states */
  html.dark .text-\\[\\#B7793B\\] { color: var(--color-accent-warm) !important; }
  html.dark .hover\\:text-\\[\\#B7793B\\]:hover { color: var(--color-accent-warm) !important; }
  html.dark .hover\\:border-\\[\\#B7793B\\]\\/50:hover { border-color: color-mix(in srgb, var(--color-accent-warm) 50%, transparent) !important; }

  /* Voice list dividers */
  html.dark .divide-\\[\\#F1F5F9\\] > :not([hidden]) ~ :not([hidden]) { border-color: var(--color-border) !important; }

  /* Solid greys outside the 50/100/200 ramp (step indicators, disabled buttons, toolbar pills) */
  html.dark .bg-zinc-300, html.dark .bg-gray-300, html.dark .bg-slate-300 { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-zinc-400, html.dark .bg-gray-400, html.dark .bg-slate-400 { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-zinc-500, html.dark .bg-gray-500, html.dark .bg-slate-500 { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-zinc-600, html.dark .bg-gray-600, html.dark .bg-slate-600 { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .bg-zinc-700, html.dark .bg-gray-700, html.dark .bg-slate-700 { background-color: color-mix(in srgb, var(--color-surface) 75%, black) !important; }
  html.dark .bg-zinc-800, html.dark .bg-gray-800, html.dark .bg-slate-800 { background-color: color-mix(in srgb, var(--color-surface) 60%, black) !important; }
  html.dark .bg-zinc-900, html.dark .bg-gray-900, html.dark .bg-slate-900 { background-color: color-mix(in srgb, var(--color-surface) 40%, black) !important; }
  html.dark .hover\\:bg-zinc-700:hover, html.dark .hover\\:bg-gray-700:hover { background-color: color-mix(in srgb, var(--color-surface) 70%, black) !important; }
  html.dark .hover\\:bg-gray-200:hover { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .hover\\:bg-gray-600:hover { background-color: var(--color-surface-ui-hover) !important; }
  /* Disabled button states */
  html.dark .disabled\\:bg-gray-300:disabled { background-color: var(--color-surface-ui-hover) !important; }
  html.dark .disabled\\:text-gray-500:disabled { color: var(--color-text-secondary) !important; }
  html.dark .disabled\\:bg-gray-300\\:disabled { background-color: var(--color-surface-ui-hover) !important; }

  /* Timeline / annotation lane colors — desaturate so they're legible but not glaring on dark canvas */
  html.dark .bg-amber-50 { background-color: rgba(228, 179, 114, 0.12) !important; }
  html.dark .bg-amber-50\\/50, html.dark .bg-amber-50\\/60 { background-color: rgba(228, 179, 114, 0.08) !important; }
  html.dark .bg-amber-200 { background-color: rgba(228, 179, 114, 0.28) !important; }
  html.dark .bg-amber-400 { background-color: rgba(228, 179, 114, 0.6) !important; }
  html.dark .bg-amber-400\\/80 { background-color: rgba(228, 179, 114, 0.55) !important; }
  html.dark .bg-amber-500\\/90 { background-color: rgba(217, 168, 101, 0.65) !important; }
  html.dark .bg-amber-800 { background-color: #92400e !important; }
  html.dark .hover\\:bg-amber-900:hover { background-color: #78350f !important; }
  html.dark .border-amber-200 { border-color: rgba(228, 179, 114, 0.35) !important; }
  html.dark .border-amber-500, html.dark .border-amber-600 { border-color: rgba(217, 168, 101, 0.55) !important; }
  html.dark .text-amber-600 { color: #fcd34d !important; }
  html.dark .text-amber-700 { color: #fcd34d !important; }
  html.dark .text-amber-800 { color: #fde68a !important; }
  html.dark .text-amber-900, html.dark .text-amber-950 { color: #fef3c7 !important; }

  html.dark .bg-sky-50 { background-color: rgba(56, 189, 248, 0.1) !important; }
  html.dark .bg-sky-50\\/60 { background-color: rgba(56, 189, 248, 0.08) !important; }
  html.dark .bg-sky-100 { background-color: rgba(56, 189, 248, 0.18) !important; }
  html.dark .bg-sky-400 { background-color: rgba(56, 189, 248, 0.65) !important; }
  html.dark .bg-sky-700\\/80 { background-color: rgba(3, 105, 161, 0.7) !important; }
  html.dark .bg-sky-900 { background-color: #0c4a6e !important; }
  html.dark .border-sky-200 { border-color: rgba(56, 189, 248, 0.3) !important; }
  html.dark .text-sky-700 { color: #7dd3fc !important; }
  html.dark .text-blue-700 { color: #93c5fd !important; }

  html.dark .bg-emerald-50 { background-color: rgba(16, 185, 129, 0.12) !important; }
  html.dark .bg-emerald-100 { background-color: rgba(16, 185, 129, 0.22) !important; }
  html.dark .bg-emerald-400 { background-color: rgba(16, 185, 129, 0.65) !important; }
  html.dark .bg-emerald-500\\/50 { background-color: rgba(16, 185, 129, 0.4) !important; }
  html.dark .bg-emerald-600\\/90 { background-color: rgba(5, 150, 105, 0.7) !important; }
  html.dark .border-emerald-500\\/50 { border-color: rgba(16, 185, 129, 0.4) !important; }
  html.dark .border-emerald-600\\/30, html.dark .border-emerald-700\\/40 { border-color: rgba(16, 185, 129, 0.32) !important; }
  html.dark .text-emerald-700 { color: #6ee7b7 !important; }
  html.dark .text-emerald-100 { color: #a7f3d0 !important; }
  html.dark .text-green-600 { color: #86efac !important; }

  html.dark .bg-teal-500 { background-color: rgba(20, 184, 166, 0.7) !important; }

  html.dark .bg-orange-300 { background-color: rgba(251, 146, 60, 0.55) !important; }
  html.dark .bg-orange-400 { background-color: rgba(251, 146, 60, 0.65) !important; }
  html.dark .bg-orange-500\\/90 { background-color: rgba(249, 115, 22, 0.65) !important; }
  html.dark .border-orange-600 { border-color: rgba(234, 88, 12, 0.5) !important; }
  html.dark .hover\\:bg-orange-300:hover { background-color: rgba(251, 146, 60, 0.5) !important; }

  html.dark .bg-yellow-500\\/80 { background-color: rgba(234, 179, 8, 0.55) !important; }
  html.dark .border-yellow-600 { border-color: rgba(202, 138, 4, 0.5) !important; }

  /* Selection halos / hard white borders on chips — bright white reads as a flash on dark */
  html.dark .ring-offset-white { --tw-ring-offset-color: var(--color-surface) !important; }
  html.dark .border-white { border-color: rgba(255, 255, 255, 0.4) !important; }
`;

/** System dark: same utility remaps, tied to logo CSS variables on `html[data-theme-preference="system"]`. */
const systemDarkOverrides = `@media (prefers-color-scheme: dark) {
${darkOverrides
  .replace(/html\.dark/g, 'html[data-theme-preference="system"]')
  .replace(/#27272a/gi, "var(--color-background)")
  .replace(/#303034/gi, "var(--color-surface)")
  .replace(/#373637/gi, "var(--color-border)")
  .replace(/#3a3a3e/gi, "var(--color-surface-ui-hover)")}
}`;

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, idToken, isLoading: authLoading } = useAuth();
  /** Avoid re-applying server theme on every idToken refresh (e.g. Cognito refresh), which reset "system" to stored "dark". */
  const themeHydratedFromApiRef = useRef(false);
  const [theme, setThemeState] = useState<ThemeMode>(() => readStoredThemeMode());
  const [activeTheme, setActiveTheme] = useState<ResolvedAppearance>(() =>
    resolveAppearance(readStoredThemeMode())
  );

  useEffect(() => {
    try {
      localStorage.removeItem("sagelyn-contrast");
    } catch {
      /* ignore */
    }
  }, []);

  const setTheme = useCallback(
    (mode: ThemeMode, options?: { fromServer?: boolean }) => {
      setThemeState(mode);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
      } catch {
        /* ignore */
      }
      if (!options?.fromServer && isAuthenticated && idToken) {
        void persistUiThemePreference(mode, idToken);
      }
    },
    [isAuthenticated, idToken]
  );

  useEffect(() => {
    const resolved = applyAppearanceClass(theme);
    setActiveTheme(resolved);
    applySagelynContrastToDocument(sagelynContrastForThemePreference(theme));
  }, [theme]);

  useEffect(() => {
    const mediaQuery = getSystemColorSchemeMql();
    if (!mediaQuery) return;
    const handleChange = () => {
      if (theme === "system") {
        const resolved = applyAppearanceClass("system");
        setActiveTheme(resolved);
        applySagelynContrastToDocument(sagelynContrastForThemePreference("system"));
      }
    };
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated) {
      themeHydratedFromApiRef.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !idToken) return;
    if (themeHydratedFromApiRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const sub = CognitoAuthService.getUserId(idToken);
        if (!sub) {
          if (!cancelled) themeHydratedFromApiRef.current = true;
          return;
        }
        const user = await fetchUserById(sub, idToken);
        if (cancelled) return;
        themeHydratedFromApiRef.current = true;
        const pref = user.ui_theme_preference;
        if (isThemeMode(pref)) {
          setThemeState(pref);
          try {
            localStorage.setItem(THEME_STORAGE_KEY, pref);
          } catch {
            /* ignore */
          }
          applyAppearanceClass(pref);
          setActiveTheme(resolveAppearance(pref));
        }
      } catch {
        if (!cancelled) themeHydratedFromApiRef.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isAuthenticated, idToken]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        activeTheme,
        themePaletteId: DEFAULT_THEME_PALETTE_ID,
      }}
    >
      <style>{darkOverrides}</style>
      <style>{systemDarkOverrides}</style>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be inside ThemeProvider");
  return context;
};

export const AppearanceMenu: React.FC<{ subItemClass: string }> = ({
  subItemClass,
}) => {
  const { theme, setTheme, activeTheme } = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const showSubmenu = isHovered || isClicked;

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsClicked(false);
      }}
    >
      <button
        type="button"
        role="menuitem"
        className={`${subItemClass} justify-between`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsClicked(!isClicked);
        }}
      >
        <div className="flex items-center gap-3">
          <span className="material-icons text-base text-zinc-400">
            {activeTheme === "dark" ? "dark_mode" : "light_mode"}
          </span>
          <div className="flex flex-col items-start leading-tight">
            <span>Appearance</span>
            <span className="text-[10px] text-zinc-400 pt-[1px] font-medium">
              {theme === "system"
                ? "System (" + (activeTheme === "dark" ? "Dark" : "Light") + ")"
                : theme === "dark"
                  ? "Dark"
                  : "Light"}
            </span>
          </div>
        </div>
        <span className="material-icons text-base text-zinc-400">chevron_right</span>
      </button>

      {showSubmenu && (
        <div className="absolute left-full bottom-0 z-[110] pl-1">
          <div className="w-44 rounded-xl border border-theme-border bg-theme-surface p-2 shadow-[0_12px_40px_-4px_rgba(0,0,0,0.14),0_4px_16px_-4px_rgba(0,0,0,0.08)] ring-1 ring-theme-border/90 dark:shadow-[0_12px_40px_-4px_rgba(0,0,0,0.45)] dark:ring-theme-border">
            <button
              type="button"
              onClick={() => setTheme("light")}
              className={`${subItemClass} justify-between !px-2 !py-2.5 hover:bg-theme-secondary/15`}
            >
              <div className="flex items-center gap-3">
                <span className="material-icons text-[18px] text-zinc-400">
                  light_mode
                </span>
                Light
              </div>
              {theme === "light" && (
                <span className="material-icons text-base text-brand-primary">check</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setTheme("dark")}
              className={`${subItemClass} justify-between !px-2 !py-2.5 hover:bg-theme-secondary/15`}
            >
              <div className="flex items-center gap-3">
                <span className="material-icons text-[18px] text-zinc-400">
                  dark_mode
                </span>
                Dark
              </div>
              {theme === "dark" && (
                <span className="material-icons text-base text-brand-primary">check</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setTheme("system")}
              className={`${subItemClass} justify-between !px-2 !py-2.5 hover:bg-theme-secondary/15`}
            >
              <div className="flex items-center gap-3">
                <span className="material-icons text-[18px] text-zinc-400">
                  desktop_windows
                </span>
                System
              </div>
              {theme === "system" && (
                <span className="material-icons text-base text-brand-primary">check</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
